const EM_DASH = '\u2014';
const EN_DASH = '\u2013';

/** Returns the number of days since the epoch for a `YYYY-MM-DD` ISO date string. */
const toEpochDays = (iso: string): number => {
  const [year, month, day] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day) / 86_400_000;
};

/** Formats a `YYYY-MM-DD` ISO date string as `M/D/YY` (no leading zeros, two-digit year). */
export const formatShortDate = (iso: string): string => {
  const [year, month, day] = iso.split('-');
  return `${Number(month)}/${Number(day)}/${year.slice(-2)}`;
};

/**
 * Formats a sorted-ascending list of `YYYY-MM-DD` ISO dates for display:
 *
 * - 0 dates -> em-dash placeholder
 * - 1 date -> `M/D/YY`
 * - >= 2 consecutive dates (every gap exactly one day) -> `M/D/YY \u2013 M/D/YY`
 * - >= 2 non-consecutive dates -> comma-separated `M/D/YY` list
 */
export const formatNotifyDates = (dates: string[]): string => {
  if (!dates || dates.length === 0) {
    return EM_DASH;
  }
  if (dates.length === 1) {
    return formatShortDate(dates[0]);
  }

  const isConsecutive = dates.every(
    (date, index) => index === 0 || toEpochDays(date) - toEpochDays(dates[index - 1]) === 1
  );
  if (isConsecutive) {
    return `${formatShortDate(dates[0])} ${EN_DASH} ${formatShortDate(dates[dates.length - 1])}`;
  }

  return dates.map(formatShortDate).join(', ');
};

type ParsedTime = { hour: number; minute: number };

const parseTime = (time: string): ParsedTime => {
  const [hour, minute] = time.split(':').map(Number);
  return { hour, minute };
};

const period = (hour: number): 'AM' | 'PM' => (hour < 12 ? 'AM' : 'PM');

const to12Hour = (hour: number): number => {
  const mod = hour % 12;
  return mod === 0 ? 12 : mod;
};

/** Renders `5`, `5:30`, etc. — hour with no leading zero, minutes only when non-zero. */
const formatTimeBody = ({ hour, minute }: ParsedTime): string =>
  minute === 0 ? `${to12Hour(hour)}` : `${to12Hour(hour)}:${String(minute).padStart(2, '0')}`;

/**
 * Formats a preferred-time window from a pair of ISO local-time strings (`HH:MM[:SS]`):
 *
 * - both AM or both PM -> single shared period suffix, e.g. `5 - 9 PM`, `5:30 - 7:30 PM`
 * - differing periods -> period on both sides, e.g. `11 AM - 2 PM`
 * - either side missing -> `Any`
 */
export const formatTimeRange = (start?: string, end?: string): string => {
  if (!start || !end) {
    return 'Any';
  }

  const startTime = parseTime(start);
  const endTime = parseTime(end);
  const startPeriod = period(startTime.hour);
  const endPeriod = period(endTime.hour);

  if (startPeriod === endPeriod) {
    return `${formatTimeBody(startTime)} - ${formatTimeBody(endTime)} ${endPeriod}`;
  }
  return `${formatTimeBody(startTime)} ${startPeriod} - ${formatTimeBody(endTime)} ${endPeriod}`;
};

/** Formats the date portion of an ISO-8601 venue-timezone datetime string as "Mon DD, YYYY". */
export const formatReservationDate = (startDateTime?: string): string => {
  if (!startDateTime) {
    return EM_DASH;
  }
  const [datePart] = startDateTime.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const dt = new Date(year, month - 1, day);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Formats the time portion of an ISO-8601 venue-timezone datetime string as "H:MM AM/PM". */
export const formatReservationTime = (startDateTime?: string): string => {
  if (!startDateTime) {
    return EM_DASH;
  }
  const timePart = startDateTime.split('T')[1]?.split(/[Z+-]/)[0];
  if (!timePart) {
    return EM_DASH;
  }
  const [hours, minutes] = timePart.split(':').map(Number);
  const dt = new Date(2000, 0, 1, hours, minutes);
  return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};
